import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../lib/auth';
import { authService, registrationService } from '../lib/api-service';
import type { ParticipantType, RegistrationFieldsConfig, RegistrationFormData } from '../lib/types';
import { formatPrice } from '../utils/participant-types';
import { DateInput } from './DateInput';
import { X } from 'lucide-react';

interface RegistrationModalProps {
  open: boolean;
  slug: string;
  partnerNames?: string[];
  fieldConfig?: RegistrationFieldsConfig;
  participantTypes?: ParticipantType[];
  paymentMethods?: string;
  onClose: () => void;
  onSuccess: () => void;
}

function fieldVisible(config: RegistrationFieldsConfig | undefined, key: string): boolean {
  if (!config || !config[key]) return true;
  return config[key].visible;
}

function fieldRequired(config: RegistrationFieldsConfig | undefined, key: string): boolean {
  if (!config || !config[key]) return false;
  return config[key].visible && config[key].required;
}

export function RegistrationModal({
  open,
  slug,
  partnerNames = [],
  fieldConfig,
  participantTypes = [],
  paymentMethods = '',
  onClose,
  onSuccess,
}: RegistrationModalProps) {
  const { user } = useAuth();
  const [form, setForm] = useState<RegistrationFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: null,
    gender: '',
    source_organization: '',
  });
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [dataSharingConsented, setDataSharingConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    // Pre-fill from profile
    authService.me().then((me) => {
      setForm((prev) => ({
        ...prev,
        first_name: me.first_name || prev.first_name,
        last_name: me.last_name || prev.last_name,
        email: me.email,
        phone: me.profile?.phone || '',
        date_of_birth: me.profile?.date_of_birth || null,
        gender: me.profile?.gender || '',
        source_organization: me.profile?.source_organization || '',
      }));
    });
  }, [open, user]);

  // Pre-select the default type whenever the participant type list changes (or modal opens).
  useEffect(() => {
    if (!open) return;
    if (participantTypes.length === 0) {
      setSelectedTypeId(null);
      return;
    }
    const def = participantTypes.find((t) => t.is_default && t.slots_remaining !== 0);
    const firstAvailable = participantTypes.find((t) => t.slots_remaining !== 0);
    setSelectedTypeId((def ?? firstAvailable ?? participantTypes[0]).id);
  }, [open, participantTypes]);

  if (!open) return null;

  const selectedType = participantTypes.find((t) => t.id === selectedTypeId) ?? null;
  const selectedIsPaid = selectedType ? parseFloat(selectedType.price) > 0 : false;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const basePayload: RegistrationFormData = {
        ...form,
        ...(selectedTypeId ? { participant_type_id: selectedTypeId } : {}),
      };
      const payload =
        partnerNames.length > 0
          ? ({ ...basePayload, data_sharing_consented: true } as RegistrationFormData & {
              data_sharing_consented: true;
            })
          : basePayload;
      await registrationService.register(slug, payload as RegistrationFormData);
      onSuccess();
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('409')) {
          // Per-type cap returns a type-specific message; surface it when present.
          setError(
            selectedType
              ? `Locurile pentru tipul „${selectedType.title}\" sunt ocupate. Alege alt tip.`
              : 'Locurile disponibile sunt ocupate sau ești deja înregistrat/ă.'
          );
        } else if (err.message.includes('400')) {
          setError('Termenul de înregistrare a expirat.');
        } else {
          setError('A apărut o eroare. Încearcă din nou.');
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  function updateField(field: keyof RegistrationFormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const inputClass =
    'w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20';

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2
            className="text-lg font-bold text-[var(--vivid-purple)]"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            Înregistrare
          </h2>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Participant type selection — only shown when types are configured */}
          {participantTypes.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Tip participant *
              </label>
              <div className="space-y-2">
                {participantTypes.map((t) => {
                  const full = t.slots_remaining === 0;
                  const isSelected = selectedTypeId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      disabled={full}
                      onClick={() => !full && setSelectedTypeId(t.id)}
                      className={`block w-full rounded-lg border p-3 text-left transition-colors ${
                        isSelected && !full
                          ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5 ring-2 ring-[var(--vivid-purple)]/20'
                          : 'border-gray-200 hover:bg-gray-50'
                      } ${full ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p
                            className={`text-sm font-semibold ${full ? 'line-through' : ''}`}
                            style={{ fontFamily: 'var(--font-primary)' }}
                          >
                            {t.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t.slots_remaining === null
                              ? 'Locuri nelimitate'
                              : full
                                ? 'Locuri ocupate'
                                : `${t.slots_remaining} ${t.slots_remaining === 1 ? 'loc disponibil' : 'locuri disponibile'}`}
                          </p>
                        </div>
                        <span className="rounded-full bg-[var(--vivid-yellow-green)]/30 px-2.5 py-0.5 text-xs font-semibold text-[var(--vivid-purple)]">
                          {formatPrice(t.price)}
                        </span>
                      </div>
                      {t.description && (
                        <div
                          className="prose prose-sm mt-2 max-w-none text-xs text-muted-foreground"
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        >
                          <ReactMarkdown>{t.description}</ReactMarkdown>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Always visible: first name, last name, email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Prenume *
              </label>
              <input
                type="text"
                required
                value={form.first_name}
                onChange={(e) => updateField('first_name', e.target.value)}
                className={inputClass}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Nume *
              </label>
              <input
                type="text"
                required
                value={form.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
                className={inputClass}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
              Email
            </label>
            <input
              type="email"
              value={form.email}
              readOnly
              className={`${inputClass} cursor-not-allowed bg-gray-50 text-muted-foreground`}
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
          </div>

          {/* Configurable fields */}
          {fieldVisible(fieldConfig, 'phone') && (
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Telefon{fieldRequired(fieldConfig, 'phone') ? ' *' : ''}
              </label>
              <input
                type="tel"
                required={fieldRequired(fieldConfig, 'phone')}
                value={form.phone || ''}
                onChange={(e) => updateField('phone', e.target.value)}
                className={inputClass}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
          )}

          {fieldVisible(fieldConfig, 'date_of_birth') && (
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Data nașterii{fieldRequired(fieldConfig, 'date_of_birth') ? ' *' : ''}
              </label>
              <DateInput
                required={fieldRequired(fieldConfig, 'date_of_birth')}
                value={form.date_of_birth || ''}
                onChange={(v) => updateField('date_of_birth', v)}
                className={inputClass}
              />
            </div>
          )}

          {fieldVisible(fieldConfig, 'gender') && (
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Gen{fieldRequired(fieldConfig, 'gender') ? ' *' : ''}
              </label>
              <select
                required={fieldRequired(fieldConfig, 'gender')}
                value={form.gender || ''}
                onChange={(e) => updateField('gender', e.target.value)}
                className={inputClass}
                style={{ fontFamily: 'var(--font-secondary)' }}
              >
                <option value="">— Selectează —</option>
                <option value="male">Masculin</option>
                <option value="female">Feminin</option>
                <option value="non_binary">Non-binar</option>
                <option value="prefer_not_to_say">Prefer să nu spun</option>
              </select>
            </div>
          )}

          {fieldVisible(fieldConfig, 'source_organization') && (
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Organizația de proveniență{fieldRequired(fieldConfig, 'source_organization') ? ' *' : ''}
              </label>
              <input
                type="text"
                required={fieldRequired(fieldConfig, 'source_organization')}
                value={form.source_organization || ''}
                onChange={(e) => updateField('source_organization', e.target.value)}
                className={inputClass}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
          )}

          {partnerNames.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-xs text-amber-800">
                Prin înregistrare, datele tale (nume, prenume, email, telefon și vârstă) vor fi transmise și organizatorilor acestei activități:{' '}
                <strong>{partnerNames.join(', ')}</strong>.
              </p>
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={dataSharingConsented}
                  onChange={(e) => setDataSharingConsented(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[var(--vivid-purple)]"
                />
                <span>
                  Sunt de acord cu partajarea datelor mele cu organizatorii menționați. <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
          )}

          {selectedIsPaid && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="mb-1 font-semibold">Plată</p>
              <p className="mb-2">Plata se va face direct cu organizatorul — noi nu procesăm plăți.</p>
              {paymentMethods && (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{paymentMethods}</ReactMarkdown>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={
              submitting ||
              (partnerNames.length > 0 && !dataSharingConsented) ||
              (participantTypes.length > 0 && (selectedTypeId === null || selectedType?.slots_remaining === 0))
            }
            className="w-full rounded-lg bg-[var(--vivid-orange)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-orange)]/90 disabled:opacity-60"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            {submitting ? 'Se înregistrează...' : 'Confirmă înregistrarea'}
          </button>
        </form>
      </div>
    </div>
  );
}
